import React from 'react';

interface LogoProps {
  className?: string;
}

export const Logo: React.FC<LogoProps> = ({ className }) => {
  return (
    <svg 
      viewBox="0 0 200 200" 
      className={className || 'w-[8.8rem] h-[8.8rem] md:w-48 md:h-48 mx-auto'}
      style={{ filter: 'drop-shadow(0 0 20px var(--color-neon-pink))' }}
    >
      <defs>
        <linearGradient id="catGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--color-neon-pink)" />
          <stop offset="100%" stopColor="var(--color-neon-cyan)" />
        </linearGradient>
        <filter id="glow">
          <feGaussianBlur stdDeviation="3" result="coloredBlur"/>
          <feMerge>
            <feMergeNode in="coloredBlur"/>
            <feMergeNode in="SourceGraphic"/>
          </feMerge>
        </filter>
      </defs>
      
      {/* Fondo circular */}
      <circle cx="100" cy="100" r="90" fill="var(--color-bg)" stroke="url(#catGradient)" strokeWidth="4" />
      
      {/* Orejas */}
      <polygon 
        points="40,50 70,90 30,90" 
        fill="var(--color-neon-pink)"
        stroke="var(--color-neon-cyan)"
        strokeWidth="2"
      />
      <polygon 
        points="160,50 130,90 170,90" 
        fill="var(--color-neon-pink)"
        stroke="var(--color-neon-cyan)"
        strokeWidth="2"
      />
      
      {/* Orejitas internas */}
      <polygon 
        points="45,60 60,85 38,85" 
        fill="var(--color-neon-cyan)"
      />
      <polygon 
        points="155,60 140,85 162,85" 
        fill="var(--color-neon-cyan)"
      />
      
      {/* Cabeza del gato */}
      <ellipse 
        cx="100" cy="100" rx="70" ry="60" 
        fill="var(--color-neon-pink)"
        stroke="var(--color-neon-cyan)"
        strokeWidth="3"
      />
      
      {/* Ojos */}
      <ellipse cx="70" cy="95" rx="18" ry="20" fill="var(--color-bg)" stroke="var(--color-neon-yellow)" strokeWidth="2" />
      <ellipse cx="130" cy="95" rx="18" ry="20" fill="var(--color-bg)" stroke="var(--color-neon-yellow)" strokeWidth="2" />
      
      {/* Pupilas */}
      <ellipse cx="70" cy="95" rx="8" ry="12" fill="var(--color-neon-yellow)" />
      <ellipse cx="130" cy="95" rx="8" ry="12" fill="var(--color-neon-yellow)" />
      
      {/* Brillo en ojos */}
      <circle cx="74" cy="90" r="3" fill="white" />
      <circle cx="134" cy="90" r="3" fill="white" />
      
      {/* Nariz */}
      <polygon 
        points="95,120 105,120 100,130" 
        fill="var(--color-neon-cyan)"
      />
      
      {/* Bigotes - lado izquierdo */}
      <line x1="30" y1="115" x2="70" y2="110" stroke="var(--color-neon-yellow)" strokeWidth="2" strokeLinecap="round" />
      <line x1="30" y1="125" x2="70" y2="125" stroke="var(--color-neon-yellow)" strokeWidth="2" strokeLinecap="round" />
      <line x1="30" y1="135" x2="70" y2="140" stroke="var(--color-neon-yellow)" strokeWidth="2" strokeLinecap="round" />
      
      {/* Bigotes - lado derecho */}
      <line x1="170" y1="115" x2="130" y2="110" stroke="var(--color-neon-yellow)" strokeWidth="2" strokeLinecap="round" />
      <line x1="170" y1="125" x2="130" y2="125" stroke="var(--color-neon-yellow)" strokeWidth="2" strokeLinecap="round" />
      <line x1="170" y1="135" x2="130" y2="140" stroke="var(--color-neon-yellow)" strokeWidth="2" strokeLinecap="round" />
      
      {/* Boca */}
      <path 
        d="M 85 135 Q 100 150 115 135" 
        fill="none"
        stroke="var(--color-neon-yellow)"
        strokeWidth="2"
        strokeLinecap="round"
      />
      
      {/* Gorro de fiesta retro */}
      <polygon 
        points="100,35 85,15 115,15" 
        fill="var(--color-neon-yellow)"
        stroke="var(--color-neon-cyan)"
        strokeWidth="2"
      />
      <circle cx="100" cy="10" r="6" fill="var(--color-neon-cyan)" />
      
      {/* Pompones del gorro */}
      <ellipse cx="82" cy="20" rx="5" ry="8" fill="var(--color-neon-cyan)" />
      <ellipse cx="118" cy="20" rx="5" ry="8" fill="var(--color-neon-cyan)" />
      
      {/* Corbata retro */}
      <polygon 
        points="100,160 90,175 100,195 110,175" 
        fill="var(--color-neon-cyan)"
        stroke="var(--color-neon-pink)"
        strokeWidth="2"
      />
      <circle cx="100" cy="165" r="5" fill="var(--color-neon-pink)" />
      
      {/* Efectos de brillo */}
      <circle cx="50" cy="50" r="5" fill="var(--color-neon-pink)" opacity="0.6" />
      <circle cx="150" cy="50" r="5" fill="var(--color-neon-cyan)" opacity="0.6" />
    </svg>
  );
};

export const LogoAnimated: React.FC = () => {
  return (
    <div className="relative inline-block animate-pulse">
      <Logo />
      <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default Logo;
